/*
 * Intercettazione globale dei metodi console.* per filtraggio per livello.
 *
 * Questo modulo sovrascrive i metodi della console (trace, debug, log, info,
 * warn, error) con wrapper che controllano il livello minimo globale prima
 * di delegare ai metodi originali.
 *
 * Perché serve?
 * Il logger di matrix-js-sdk (basato su loglevel) risolve i metodi console.*
 * al momento della chiamata (non all'inizializzazione). Questo ci permette di
 * intercettare i suoi log semplicemente sovrascrivendo console.*.
 * In questo modo controlliamo TUTTI i log (SDK incluso) senza dover
 * modificare i ~80+ file che importano il logger dell'SDK.
 *
 * Ordine di esecuzione previsto:
 * 1. Al caricamento del modulo → cattura dei metodi console originali
 * 2. Alla prima chiamata di setGlobalMinLevel() → installazione degli intercettori
 * 3. Rageshake può ulteriormente avvolgere console.* per la persistenza su IndexedDB
 *
 * IMPORTANTE: i riferimenti originali vengono catturati al tempo di caricamento
 * del modulo, PRIMA che rageshake o altri sistemi li sovrascrivano.
 */

import { LogLevel, isLevelEnabled } from "./LogLevel";

// ─── Stato globale ───────────────────────────────────────────────────────────

/**
 * Livello minimo globale per l'output della console.
 * I messaggi con priorità inferiore vengono soppressi.
 * Il valore predefinito è Debug (tutto visibile).
 */
let globalMinLevel: LogLevel = LogLevel.Debug;

/** Flag per garantire che l'installazione avvenga una sola volta. */
let installed = false;

// ─── Riferimenti ai metodi originali della console ──────────────────────────
// Catturati subito al caricamento del modulo, prima di qualsiasi monkey-patch.

const nativeConsole = {
    trace: console.trace.bind(console),
    debug: console.debug.bind(console),
    log: console.log.bind(console),
    info: console.info.bind(console),
    warn: console.warn.bind(console),
    error: console.error.bind(console),
};

// ─── Mappatura metodo console → livello di log ──────────────────────────────

/**
 * Associa ogni metodo della console al livello di log corrispondente.
 *   trace, debug → Debug
 *   log, info    → Info
 *   warn         → Warn
 *   error        → Error
 */
const consoleMethodToLevel: Record<string, LogLevel> = {
    trace: LogLevel.Debug,
    debug: LogLevel.Debug,
    log: LogLevel.Info,
    info: LogLevel.Info,
    warn: LogLevel.Warn,
    error: LogLevel.Error,
};

// ─── API pubblica ───────────────────────────────────────────────────────────

/**
 * Verifica se un messaggio al livello dato dovrebbe essere stampato in console,
 * confrontandolo con il livello minimo globale corrente.
 */
export function shouldLog(level: LogLevel): boolean {
    return isLevelEnabled(level, globalMinLevel);
}

/**
 * Restituisce il livello minimo globale attualmente configurato.
 */
export function getGlobalMinLevel(): LogLevel {
    return globalMinLevel;
}

/**
 * Imposta il livello minimo globale per l'output della console.
 *
 * Alla prima invocazione, installa gli intercettori sui metodi console.*.
 * Le chiamate successive aggiornano solo il livello senza reinstallare.
 *
 * @param level - Il nuovo livello minimo da applicare globalmente.
 */
export function setGlobalMinLevel(level: LogLevel): void {
    installInterceptors();
    globalMinLevel = level;
}

// ─── Installazione intercettori ─────────────────────────────────────────────

/**
 * Sostituisce i metodi console.* con versioni filtrate.
 *
 * Ogni wrapper:
 *  1. Controlla shouldLog() per il livello associato al metodo
 *  2. Se abilitato → delega al metodo nativo originale
 *  3. Se disabilitato → il messaggio viene silenziosamente scartato
 *
 * L'installazione avviene una sola volta; chiamate successive sono no-op.
 */
function installInterceptors(): void {
    if (installed) return;
    installed = true;

    for (const [method, level] of Object.entries(consoleMethodToLevel)) {
        const nativeMethod = nativeConsole[method as keyof typeof nativeConsole];
        (console as any)[method] = (...args: unknown[]): void => {
            if (shouldLog(level)) {
                nativeMethod(...args);
            }
        };
    }
}
